import { useMemo } from 'react';
import { Mapping, MappingType } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/mapping.pb';
import { DataSource } from '@weave/schema-gen-ts/dist/schemas/syncapp/integrations-service/datasource.pb';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import { useQueries } from 'react-query';
import {
  type ApptRequestConfig,
  type AppointmentType as AppointmentTypeV2,
  getApptRequestConfig,
  getAppointmentTypes,
} from '@frontend/api-appointment-types';
import { DataSourcesQueries } from '@frontend/api-data-sources';
import { getOfficeHours } from '@frontend/api-office-hours';
import { Weekday, Schedule } from '@frontend/api-schedule-availability';
import { useGetOfficeExceptionsQuery, getDateTime, getDisplayDate } from '@frontend/api-schedule-exceptions';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { type ServiceProvider, listServiceProviders } from '@frontend/api-service-providers';
import { useOrgData } from '@frontend/get-org-data';
import { i18next, useTranslation } from '@frontend/i18n';
import { useAppScopeStore } from '@frontend/scope';
import { formatV2AppointmentHours } from './schedule-v2-utils';
import { AppointmentHour, AppointmentType, Exception, Provider } from './types';

dayjs.extend(timezone);

const getTimezoneLabel = (tz?: string) => ({
  label: i18next.t('Timezone', { ns: 'settings' }),
  value: dayjs().tz(tz).format('z'),
});

const formatExceptionTime = (time: string) => time.toUpperCase().replace('AM', ' AM').replace('PM', ' PM');

const sortByName = (a: Provider, b: Provider) => {
  if (a.lastName === b.lastName) {
    return a.firstName?.localeCompare(b.firstName);
  }
  return a.lastName?.localeCompare(b.lastName);
};
// Lead time in milliseconds before the appointment
const V2_HOUR_CONVERSION = 60 * 60; // milliseconds in an hour
const V2_DAY_CONVERSION = 60 * 60 * 24; // milliseconds in a day

// Lead time in minutes before the appointment
const V3_HOUR_CONVERSION = 60; // minutes in an hour
const V3_DAY_CONVERSION = 60 * 24; // minutes in a day

export const useWeaveAssistantScheduleSettings = () => {
  const { t } = useTranslation('settings');
  const { locationData, selectedLocationIds: globalSelectedLocationIds } = useAppScopeStore();
  const { organizationsData } = useOrgData();

  ////////////////////////// SCHEDULE DATA //////////////////////////////
  const { isScheduleV3FlagEnabled } = SchedulerV3.Helpers.useGetSchedulerV3FeatureFlagDetails();

  // v2 API call
  const scheduleV2Queries = useQueries(
    globalSelectedLocationIds.map((locationId) => {
      const response = {
        queryKey: ['schedule', locationId, 'officeClosedTimes'], // TODO: replace with queryKeys.officeClosedTimes(locationId)
        queryFn: () => getOfficeHours(locationId),
        retry: 1,
        enabled: !isScheduleV3FlagEnabled && !!locationId,
        onSettled: () => SchedulerV3.trackScheduleAPIVersionChange('v2', { endpoint: 'getOfficeHours' }),
        select: (data: Schedule) => ({ locationId, appointmentHours: data }),
      };
      return { ...response, locationId };
    })
  );

  const appointmentHours = useMemo(
    () =>
      isScheduleV3FlagEnabled
        ? {}
        : scheduleV2Queries.reduce<Record<string, AppointmentHour[]>>((acc, location) => {
            const schedule = location.data?.appointmentHours;
            const locationId = location.data?.locationId;
            if (!locationId || !schedule) return acc;
            const tzLabelObj = getTimezoneLabel(locationData[locationId]?.timezone);

            return {
              ...acc,
              [locationId]: [
                tzLabelObj,
                ...formatV2AppointmentHours(Weekday.monday, schedule),
                ...formatV2AppointmentHours(Weekday.tuesday, schedule),
                ...formatV2AppointmentHours(Weekday.wednesday, schedule),
                ...formatV2AppointmentHours(Weekday.thursday, schedule),
                ...formatV2AppointmentHours(Weekday.friday, schedule),
                ...formatV2AppointmentHours(Weekday.saturday, schedule),
                ...formatV2AppointmentHours(Weekday.sunday, schedule),
              ],
            };
          }, {}),

    [isScheduleV3FlagEnabled, locationData, scheduleV2Queries]
  );

  const appointmentHoursIsLoading = useMemo(
    () => (isScheduleV3FlagEnabled ? false : scheduleV2Queries.some((query) => query.isLoading)),
    [isScheduleV3FlagEnabled, scheduleV2Queries]
  );

  const getAppointmentHoursByLocationId = (locationId: string) => appointmentHours[locationId];
  ///////////////////////// END SCHEDULE DATA //////////////////////////

  //////////////////////// EXCEPTION DATA //////////////////////////////
  // v2 API call
  const { data: exceptionV2Data, isLoading: exceptionsV2IsLoading } = useGetOfficeExceptionsQuery({
    locationIds: globalSelectedLocationIds,
  });

  const exceptions: Record<string, Exception[]> = useMemo(() => {
    // v3 exception formatting uses the same formattedV3Schedule data from above
    if (isScheduleV3FlagEnabled) return {};

    // v2 exception formatting
    if (!exceptionV2Data || !exceptionV2Data.data) return {};
    return (
      exceptionV2Data.data
        .sort((a, b) => {
          const dateA = dayjs(a.startDateTime);
          const dateB = dayjs(b.startDateTime);
          if (dateA.isBefore(dateB)) return -1;
          if (dateA.isAfter(dateB)) return 1;
          return 0;
        })
        .reduce<Record<string, Exception[]>>((acc, exception) => {
          if (!exception?.startDateTime || !exception?.endDateTime || !exception.locationId) return acc;
          const [startDate, endDate, startTime, endTime] = getDateTime(
            exception?.startDateTime,
            exception?.endDateTime
          );

          const locationId = exception.locationId;

          const newException: Exception = {
            availability: exception.isAvailable ? t('Available') : t('Unavailable'),
            date: getDisplayDate(exception.startDateTime, exception.endDateTime),
            name: exception.name ?? '',
            time:
              // If all day event, show "All day"
              startDate === endDate && startTime === '12:00am' && endTime === '11:59pm'
                ? t('All day')
                : startTime === endTime
                ? formatExceptionTime(startTime)
                : `${formatExceptionTime(startTime)} - ${formatExceptionTime(endTime)}`,
          };

          return {
            ...acc,
            [locationId]: [...(acc[locationId] || []), newException],
          };
        }, {}) ?? {}
    );
  }, [exceptionV2Data, isScheduleV3FlagEnabled, t]);

  const exceptionsIsLoading = useMemo(
    () => (isScheduleV3FlagEnabled ? false : exceptionsV2IsLoading),
    [exceptionsV2IsLoading, isScheduleV3FlagEnabled]
  );

  const getExceptionsByLocationId = (locationId: string) => exceptions[locationId] || [];
  //////////////////////// END EXCEPTION DATA //////////////////////////

  /////////////////////// ADVANCED BOOKING REQUIREMENT DATA ///////////////////
  // v2 API call
  const bookingRequirementsV2Queries = useQueries(
    globalSelectedLocationIds.map((locationId) => {
      const results = {
        queryKey: ['schedule', 'apptReqConfigData', locationId],
        queryFn: () => getApptRequestConfig(locationId),
        retry: 1,
        enabled: !isScheduleV3FlagEnabled && !!locationId,
        select: (data: ApptRequestConfig) => ({
          locationId,
          requestBufferDuration: data.requestBufferDuration || 0,
        }),
      };

      return { ...results, locationId };
    })
  );

  const { readyLocationIds: OSReadyLocationIds } = SchedulerV3.Helpers.useOnlineSchedulingFeatureReadiness();

  // v3 API call
  const { data: bookingRequirementsV3Data, isLoading: bookingRequirementsV3IsLoading } =
    SchedulerV3.Queries.useListLocationSettingsQuery({
      request: {
        locationIds: OSReadyLocationIds,
      },
      options: {
        enabled: OSReadyLocationIds.length > 0,
        select: (data) => data.locationsSettings,
      },
    });

  const advancedBookingRequirement = useMemo(() => {
    if (isScheduleV3FlagEnabled) {
      const newAdvancedBookingRequirementsV3: Record<string, string> = {};
      for (const locationId in bookingRequirementsV3Data) {
        const currentLeadDuration = bookingRequirementsV3Data[locationId].leadDuration;
        if (!currentLeadDuration) {
          newAdvancedBookingRequirementsV3[locationId] = 'N/A';
          continue;
        }
        let countString = t('{{count}} Days', { count: Math.floor(currentLeadDuration / V3_DAY_CONVERSION) });

        // if count is less than 1 day, show in hours
        if (currentLeadDuration < V3_DAY_CONVERSION) {
          countString = t('{{count}} Hours', { count: Math.floor(currentLeadDuration / V3_HOUR_CONVERSION) });
        }
        newAdvancedBookingRequirementsV3[locationId] = countString;
      }
      return newAdvancedBookingRequirementsV3;
    }

    return bookingRequirementsV2Queries.reduce<Record<string, string>>((acc, location) => {
      const locationId = location.data?.locationId;
      // requestBufferDuration can be 0, so don't check for falsy
      if (!locationId || location.data?.requestBufferDuration === undefined) return acc;
      const count = location.data.requestBufferDuration;
      let countString = t('{{count}} Days', { count: Math.floor(count / V2_DAY_CONVERSION) });

      // if count is less than 1 day, show in hours
      if (count < V2_DAY_CONVERSION) {
        countString = t('{{count}} Hours', { count: Math.floor(count / V2_HOUR_CONVERSION) });
      }

      return { ...acc, [locationId]: countString };
    }, {});
  }, [bookingRequirementsV2Queries, bookingRequirementsV3Data, isScheduleV3FlagEnabled, t]);

  const advancedBookingRequirementIsLoading = useMemo(
    () =>
      isScheduleV3FlagEnabled
        ? bookingRequirementsV3IsLoading
        : bookingRequirementsV2Queries.some((query) => query.isLoading),
    [bookingRequirementsV2Queries, bookingRequirementsV3IsLoading, isScheduleV3FlagEnabled]
  );

  const getAdvancedBookingRequirementByLocationId = (locationId: string) =>
    advancedBookingRequirement[locationId] || '';
  ///////////////////// END ADVANCED BOOKING REQUIREMENT DATA /////////////////

  //////////////////////// APPOINTMENT TYPE DATA /////////////////////////
  // v3 API call
  const appointmentTypesV3Queries = SchedulerV3.Queries.useListAppointmentTypesMultiQueries(
    globalSelectedLocationIds.map((locationId) => ({
      request: {
        locationsIds: [locationId],
        parentLocationId: organizationsData.find((location) => location.locationId === locationId)?.parentId || '',
        showHidden: true,
      },
      options: {
        enabled: !!locationId && isScheduleV3FlagEnabled,
        select: (data) => ({ locationId, appointmentTypes: data.appointmentTypes }),
      },
    }))
  );
  const listMappingsQuery = SchedulerV3.Queries.useListMappingsQueries(
    globalSelectedLocationIds.map((selectedLocationId) => ({
      request: {
        locationIds: [selectedLocationId],
        mappingType: MappingType.PROVIDER_APPOINTMENT_TYPE,
        showInactive: true,
      },
      options: {
        enabled: !!selectedLocationId && isScheduleV3FlagEnabled,
        select: (data) => data.mappings || [],
      },
    }))
  );
  const listMappings = listMappingsQuery
    .flatMap((list) => list.data)
    .reduce<Record<string, Mapping[]>>((acc, mapping) => {
      if (!mapping) return acc;
      const id = mapping.appointmentTypeMetaData?.id;
      if (!id) return acc;
      const newValue = acc[id] || [];
      return { ...acc, [id]: [...newValue, mapping] };
    }, {});

  // v2 API call
  const appointmentTypesV2Queries = useQueries(
    globalSelectedLocationIds.map((locationId) => {
      const results = {
        queryKey: ['appointmentTypes', locationId],
        queryFn: () => {
          return getAppointmentTypes(locationId);
        },
        enabled: !!locationId && !isScheduleV3FlagEnabled,
        onSettled: () => SchedulerV3.trackScheduleAPIVersionChange('v2', { endpoint: 'getAppointmentTypes' }),
        select: (data: AppointmentTypeV2[]) => ({ locationId, appointmentTypes: data }),
      };

      return { ...results, locationId };
    })
  );

  const appointmentTypes = useMemo(() => {
    if (isScheduleV3FlagEnabled) {
      return appointmentTypesV3Queries.reduce<Record<string, AppointmentType[]>>((acc, location) => {
        const locationId = location.data?.locationId;
        if (!locationId || !location.data?.appointmentTypes) return acc;
        const newAppointmentTypes = location.data.appointmentTypes.map((type) => {
          const matchingAppointmentId = listMappings[type.id];
          return {
            duration: t('{{duration}} mins', { duration: type.details?.dur }),
            name: type.displayName ?? '',
            paymentAmount: type.details?.amt ?? 0,
            providers: matchingAppointmentId?.map((data) => data.providerMetaData?.name).join(', ') || '',
          };
        });

        return {
          ...acc,
          [locationId]: newAppointmentTypes,
        };
      }, {});
    }

    return appointmentTypesV2Queries.reduce<Record<string, AppointmentType[]>>((acc, location) => {
      const locationId = location.data?.locationId;
      if (!locationId || !location.data?.appointmentTypes) return acc;
      const types = location.data.appointmentTypes.map((type) => ({
        duration: t('{{duration}} mins', { duration: type.durationMinutes }),
        name: type.name,
        paymentAmount: type.hasGlobalBookingDeposit
          ? type.globalBookingDepositAmount
          : type.hasBookingDeposit
          ? type.bookingDepositAmount
          : 0,
        providers: type.providerAssets
          .filter((provider) => provider.available)
          .map((provider) => provider.providerName)
          .join(', '),
      }));

      return {
        ...acc,
        [locationId]: types,
      };
    }, {});
  }, [appointmentTypesV2Queries, appointmentTypesV3Queries, isScheduleV3FlagEnabled, listMappings, t]);

  const appointmentTypesIsLoading = useMemo(
    () =>
      isScheduleV3FlagEnabled
        ? appointmentTypesV3Queries.some((query) => query.isLoading)
        : appointmentTypesV2Queries.some((query) => query.isLoading),
    [appointmentTypesV2Queries, appointmentTypesV3Queries, isScheduleV3FlagEnabled]
  );

  const getAppointmentTypesByLocationId = (locationId: string) => appointmentTypes[locationId] || [];
  ///////////////////////// END APPOINTMENT TYPE DATA /////////////////////

  //////////////////////// PROVIDER DATA //////////////////////////////
  // TODO: add pagination for providers list if needed
  // v3 API call
  const providersV3Queries = SchedulerV3.Queries.useListProvidersMultiQueries(
    globalSelectedLocationIds.map((locationId) => ({
      request: {
        locationIds: [locationId],
        parentLocationId: organizationsData.find((location) => location.locationId === locationId)?.parentId || '',
      },
      options: {
        enabled: isScheduleV3FlagEnabled && !!locationId,
        select: (data) => data.providers,
      },
    }))
  );
  const providersV3 = providersV3Queries.map((q) => q.data || []);

  const { data: integrations, isLoading: integrationsIsLoading } = DataSourcesQueries.useGetAccessibleIntegrations();
  const integrationsMap = useMemo<Record<string, DataSource>>(
    () =>
      integrations?.reduce(
        (map, integration) => ({
          ...map,
          [integration.SourceID]: integration,
        }),
        {}
      ) ?? {},
    [integrations]
  );
  const providersV2Queries = useQueries(
    globalSelectedLocationIds.map((locationId) => {
      return {
        queryKey: ['schedule', 'scheduleProvidersList', locationId], // TODO: replace with queryKeys.providersList([locationId])
        queryFn: () => listServiceProviders(locationId),
        onSettled: () => SchedulerV3.trackScheduleAPIVersionChange('v2', { endpoint: 'listServiceProviders' }), // TODO: do we need this here?
        select: (data: ServiceProvider[]) => ({ locationId, providers: data }),
      };
    })
  );

  const providers = useMemo(() => {
    if (isScheduleV3FlagEnabled) {
      return providersV3.reduce<Record<string, Provider[]>>((acc, current) => {
        const locationId = current[0]?.locationId;
        const newProviders = current
          .map((provider) => ({
            displayName: provider.displayName || '',
            firstName: provider.firstName || '',
            image: provider.profile?.picUrl || undefined,
            lastName: provider.lastName || '',
            source: provider.sourceId ? integrationsMap[provider.sourceId]?.sourceName ?? '' : 'Weave',
          }))
          .sort(sortByName);

        return {
          ...acc,
          [locationId]: newProviders,
        };
      }, {});
    }

    return providersV2Queries?.reduce<Record<string, Provider[]>>((acc, location) => {
      const locationId = location.data?.locationId;
      const newProviders = location.data?.providers
        .map((provider) => ({
          displayName: provider.publicDisplayName,
          firstName: provider.firstName,
          image: provider.publicDisplayImage || undefined,
          lastName: provider.lastName,
          source: provider.sourceId ? integrationsMap[provider.sourceId]?.sourceName ?? '' : 'Weave',
        }))
        .sort(sortByName);
      if (!locationId || !newProviders) return acc;
      return {
        ...acc,
        [locationId]: newProviders,
      };
    }, {});
  }, [integrationsMap, isScheduleV3FlagEnabled, providersV2Queries, providersV3]);

  const providersIsLoading = useMemo(
    () =>
      integrationsIsLoading ||
      (isScheduleV3FlagEnabled
        ? providersV3Queries.some((query) => query.isLoading)
        : providersV2Queries.some((query) => query.isLoading)),
    [integrationsIsLoading, isScheduleV3FlagEnabled, providersV2Queries, providersV3Queries]
  );

  const getProvidersByLocationId = (locationId: string) => providers?.[locationId] || [];
  //////////////////////// END PROVIDER DATA //////////////////////////

  return {
    advancedBookingRequirement,
    advancedBookingRequirementIsLoading,
    appointmentHours,
    appointmentHoursIsLoading,
    appointmentTypes,
    appointmentTypesIsLoading,
    exceptions,
    exceptionsIsLoading,
    getAdvancedBookingRequirementByLocationId,
    getAppointmentHoursByLocationId,
    getAppointmentTypesByLocationId,
    getExceptionsByLocationId,
    getProvidersByLocationId,
    providers,
    providersIsLoading,
  };
};

// TODO: do we need to add pagination for the providers list, exceptions, etc.?
